import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import styles from './styles/TableOfContents.module.css';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import CanvasContext from "../../../context/CanvasContext";
import TableOfContentsModal from "./TableOfContentsModal";
import useIsMobile from "../../../utils/useIsMobile";
import { FaArrowLeft, FaPlus, FaTrashAlt, FaSave, FaTimes, FaGripVertical, FaMinus } from 'react-icons/fa';

const defaultTocStructure = { contents: [{ sections: [], items: [] }] };

const TableOfContents = () => {
    const { scenario, setScenario, situations, setShowTocEditor } = useContext(CanvasContext);
    const navigate = useNavigate();

    const [tableOfContents, setTableOfContents] = useState(() => {
        const initialToc = scenario?.tableOfContents;
        if (!initialToc || !initialToc.contents || initialToc.contents.length === 0 || !Array.isArray(initialToc.contents[0]?.sections)) {
            const validStructure = defaultTocStructure;
            if (initialToc?.contents?.[0]?.sections) {
                 validStructure.contents[0].sections = initialToc.contents[0].sections.map(sec => ({
                    ...sec,
                    items: Array.isArray(sec.items) ? sec.items : []
                 }));
            }
            return validStructure;
        }
        initialToc.contents[0].sections = initialToc.contents[0].sections.map(sec => ({
            ...sec,
            items: Array.isArray(sec.items) ? sec.items : []
        }));
        return initialToc;
    });

    const [isSectionInputVisible, setIsSectionInputVisible] = useState(false);
    const [isEditingSection, setIsEditingSection] = useState(false);
    const [sectionLabel, setSectionLabel] = useState("");
    const [selectedSituation, setSelectedSituation] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [itemLabel, setItemLabel] = useState('');
    const [currentSectionIndex, setCurrentSectionIndex] = useState(null);
    const [currentItemIndex, setCurrentItemIndex] = useState(null);
    const [editingSectionOriginalLabel, setEditingSectionOriginalLabel] = useState("");

    const inputRef = useRef(null);

    useEffect(() => {
        if (scenario?.tableOfContents) {
            const scenarioToc = scenario.tableOfContents;
             if (!scenarioToc || !scenarioToc.contents || scenarioToc.contents.length === 0 || !Array.isArray(scenarioToc.contents[0]?.sections)) {
                setTableOfContents(defaultTocStructure);
             } else {
                scenarioToc.contents[0].sections = scenarioToc.contents[0].sections.map(sec => ({
                    ...sec,
                    items: Array.isArray(sec.items) ? sec.items : []
                }));
                setTableOfContents(scenarioToc);
             }
        } else {
             setTableOfContents(defaultTocStructure);
        }
    }, [scenario?.tableOfContents]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (inputRef.current && !inputRef.current.contains(event.target) && isEditingSection) {
                 const editControls = inputRef.current.closest(`.${styles.sectionEdit}`);
                 if (!editControls || !editControls.contains(event.target)) {
                    cancelEditing();
                 }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isEditingSection]);

    
    const saveTableOfContents = async (newTableOfContents) => {
        if (!scenario?._id) {
            console.error("Scenario ID is missing, cannot save.");
            return;
        }
        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/${scenario._id}/update-toc`,
                { tableOfContents: newTableOfContents }
            );
            setTableOfContents(response.data.tableOfContents);
            setScenario((prevScenario) => ({
                ...prevScenario,
                tableOfContents: response.data.tableOfContents,
            }));
            console.log("Table of Contents saved successfully.");
        } catch (error) {
            console.error("Failed to save Table of Contents:", error);

        }
    };

    

    const createSection = () => {
        setIsSectionInputVisible(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleAddSection = async () => {
        if (!sectionLabel.trim()) {
            console.log("Section label cannot be empty.");
            return;
        }

        const currentToc = tableOfContents || defaultTocStructure;
        if (!currentToc.contents || currentToc.contents.length === 0) {
            currentToc.contents = [ { sections: [], items: [] } ];
        }
        if (!currentToc.contents[0].sections) {
             currentToc.contents[0].sections = [];
        }

        const updatedTableOfContents = JSON.parse(JSON.stringify(currentToc));
        const newSection = { label: sectionLabel.trim(), items: [] };
        updatedTableOfContents.contents[0].sections.push(newSection);

        await saveTableOfContents(updatedTableOfContents);

        setIsSectionInputVisible(false);
        setSectionLabel('');
    };

    const handleKeyPressSection = (e) => {
        if (e.key === "Enter") {
            handleAddSection();
        }
    };

    const startEditing = (sectionIndex, label) => {
        setCurrentSectionIndex(sectionIndex);
        setIsEditingSection(true);
        setSectionLabel(label);
        setEditingSectionOriginalLabel(label);
        setIsSectionInputVisible(false);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSectionEditConfirm = async (sectionIndex) => {
        if (!sectionLabel.trim()) {
             console.log("Section label cannot be empty.");
             setSectionLabel(editingSectionOriginalLabel);
             setIsEditingSection(false);
             setCurrentSectionIndex(null);
             return;
        }
        if (sectionLabel.trim() === editingSectionOriginalLabel) {
            cancelEditing();
            return;
        }

        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));
        if (updatedTableOfContents?.contents?.[0]?.sections?.[sectionIndex]) {
            updatedTableOfContents.contents[0].sections[sectionIndex].label = sectionLabel.trim();
            await saveTableOfContents(updatedTableOfContents);
        } else {
            console.error("Error updating section: Invalid structure or index.");
        }

        cancelEditing();
    };

    const handleSectionEditKeyPress = (e, sectionIndex) => {
         if (e.key === "Enter") {
             handleSectionEditConfirm(sectionIndex);
         } else if (e.key === "Escape") {
             cancelEditing();
         }
     };

    const cancelEditing = () => {
        setIsEditingSection(false);
        setCurrentSectionIndex(null);
        setSectionLabel("");
        setEditingSectionOriginalLabel("");
    };


    const handleDeleteSection = async (sectionIndexToDelete) => {
        if (!window.confirm(`Are you sure you want to delete the section "${tableOfContents.contents[0].sections[sectionIndexToDelete].label}" and all its items?`)) {
            return;
        }

        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));
        if (updatedTableOfContents?.contents?.[0]?.sections) {
            updatedTableOfContents.contents[0].sections.splice(sectionIndexToDelete, 1);
            await saveTableOfContents(updatedTableOfContents);
        } else {
             console.error("Error deleting section: Invalid structure or index.");
        }
    };

    

    const toggleModal = (event, sectionIndex, itemIndex = null, item = null) => {
        if (event) {
            event.stopPropagation();
        }

        setIsModalVisible(!isModalVisible);

        if (!isModalVisible) {
            setCurrentSectionIndex(sectionIndex);
            setCurrentItemIndex(itemIndex);
            setItemLabel(item ? item.label : '');
            const currentSituation = item && item.situation ? situations.find(sit => sit._id === item.situation) : null;
            setSelectedSituation(currentSituation || null);
        } else {
             setCurrentSectionIndex(null);
             setCurrentItemIndex(null);
             setItemLabel('');
             setSelectedSituation(null);
        }
    };

    const handleSaveItem = async () => {
        if (!itemLabel.trim() || !selectedSituation?._id) {
            alert("Please provide a label and select a situation.");
            return;
        }

        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));
        const targetSection = updatedTableOfContents?.contents?.[0]?.sections?.[currentSectionIndex];

        if (!targetSection) {
             console.error("Error saving item: Target section not found.");
             setIsModalVisible(false);
             setCurrentSectionIndex(null);
             setCurrentItemIndex(null);
             setItemLabel('');
             setSelectedSituation(null);
             return;
        }
         if (!Array.isArray(targetSection.items)) {
            targetSection.items = [];
        }


        const newItemData = { label: itemLabel.trim(), situation: selectedSituation._id };

        if (currentItemIndex === null) {
            targetSection.items.push(newItemData);
        } else {
            if (currentItemIndex >= 0 && currentItemIndex < targetSection.items.length) {
                 targetSection.items[currentItemIndex] = newItemData;
            } else {
                 console.error("Error updating item: Invalid item index.");
                 setIsModalVisible(false);
                 setCurrentSectionIndex(null);
                 setCurrentItemIndex(null);
                 setItemLabel('');
                 setSelectedSituation(null);
                 return;
            }
        }

        await saveTableOfContents(updatedTableOfContents);

        setIsModalVisible(false);
        setItemLabel('');
        setSelectedSituation(null);
        setCurrentSectionIndex(null);
        setCurrentItemIndex(null);
    };

    const handleDeleteItem = async (sectionIndex, itemIndexToDelete) => {

        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));
        const targetSection = updatedTableOfContents?.contents?.[0]?.sections?.[sectionIndex];

        if (targetSection?.items && itemIndexToDelete >= 0 && itemIndexToDelete < targetSection.items.length) {
            targetSection.items.splice(itemIndexToDelete, 1);
            await saveTableOfContents(updatedTableOfContents);
        } else {
            console.error("Error deleting item: Invalid structure or index.");
        }
    };

    

    const onDragEnd = (result) => {
        const { source, destination, type } = result;

        if (!destination || !tableOfContents?.contents?.[0]) {
            return;
        }

        const sourceContentIndex = 0;
        const destinationContentIndex = 0; 

         if (!tableOfContents.contents[sourceContentIndex]?.sections) {
             console.error("DragEnd Error: Source sections array is missing.");
             return;
         }

        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));
        const sections = updatedTableOfContents.contents[sourceContentIndex].sections;


         if (type === 'section') {
             if (source.droppableId === destination.droppableId && source.index === destination.index) {
                 return;
             }
            if (source.droppableId === 'toc-sections' && destination.droppableId === 'toc-sections') {
                 const [reorderedSection] = sections.splice(source.index, 1);
                 sections.splice(destination.index, 0, reorderedSection);

                 saveTableOfContents(updatedTableOfContents);
            } else {
                 console.warn("Unsupported section drag operation between different droppables.");
            }
         }
        else if (type === 'item') {
            const sourceDroppableIdParts = source.droppableId.split('-');
            const destinationDroppableIdParts = destination.droppableId.split('-');

            if (sourceDroppableIdParts.length < 2 || destinationDroppableIdParts.length < 2) {
                 console.error("DragEnd Error: Invalid droppable ID format for items.");
                 return;
            }

            const sourceSectionIndex = parseInt(sourceDroppableIdParts[1], 10);
            const destinationSectionIndex = parseInt(destinationDroppableIdParts[1], 10);

             if (isNaN(sourceSectionIndex) || isNaN(destinationSectionIndex) ||
                 sourceSectionIndex < 0 || sourceSectionIndex >= sections.length ||
                 destinationSectionIndex < 0 || destinationSectionIndex >= sections.length) {
                  console.error("DragEnd Error: Invalid section index parsed from droppable ID.");
                  return;
             }
              if (!sections[sourceSectionIndex]?.items) {
                   console.error("DragEnd Error: Source section items array is missing.");
                   return;
              }
              if (!sections[destinationSectionIndex]?.items) {
                  console.error("DragEnd Error: Destination section items array is missing.");
                   if(sections[destinationSectionIndex]) {
                       sections[destinationSectionIndex].items = [];
                   } else {
                       return;
                   }
              }

            if (source.droppableId === destination.droppableId) {
                if (source.index === destination.index) {
                    return;
                }
                const sectionItems = sections[sourceSectionIndex].items;
                const [movedItem] = sectionItems.splice(source.index, 1);
                sectionItems.splice(destination.index, 0, movedItem);
            }
            else {
                const sourceSectionItems = sections[sourceSectionIndex].items;
                 const destinationSectionItems = sections[destinationSectionIndex].items;

                 if (!Array.isArray(destinationSectionItems)) {
                      sections[destinationSectionIndex].items = [];
                 }

                const [movedItem] = sourceSectionItems.splice(source.index, 1);
                sections[destinationSectionIndex].items.splice(destination.index, 0, movedItem);
            }

            saveTableOfContents(updatedTableOfContents);
        }
    };


    const currentSections = tableOfContents?.contents?.[0]?.sections || [];


    return (
        <div className={styles.container}>
            <div className={styles.contentWrapper}>
                <button onClick={() => setShowTocEditor(false)} className={`${styles.button} ${styles.buttonText} ${styles.backButton}`} title="Back to Editor">
                    <FaArrowLeft />
                    <span>Back</span>
                </button>

                <div className={styles.header}>
                    <h1 className={styles.title}>Table of Contents</h1>
                </div>

                {/* --- Create Section Controls --- */}
                 {!isSectionInputVisible && (
                     <div className={styles.controls}>
                         <button onClick={createSection} className={`${styles.button} ${styles.buttonPrimary}`}>
                             <FaPlus /> Create Section
                         </button>
                     </div>
                )}

                {isSectionInputVisible && (
                    <div className={styles.sectionInputContainer}>
                         <div className={styles.sectionInput} ref={inputRef}>
                            <input
                                type="text"
                                value={sectionLabel}
                                onChange={(e) => setSectionLabel(e.target.value)}
                                onKeyPress={handleKeyPressSection}
                                placeholder="Enter new section title"
                                className={styles.inputField}
                                aria-label="New section title"
                            />
                            <div className={styles.buttonContainer}>
                                <button onClick={handleAddSection} className={`${styles.button} ${styles.buttonPrimary}`} title="Create Section">
                                    <FaPlus /> Create
                                </button>
                                <button onClick={() => { setIsSectionInputVisible(false); setSectionLabel(''); }} className={`${styles.button} ${styles.buttonSecondary}`} title="Cancel">
                                    <FaTimes /> Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- Modal --- */}
                 {isModalVisible && (
                    <TableOfContentsModal
                        isVisible={isModalVisible}
                        toggleModal={toggleModal}
                        itemLabel={itemLabel}
                        setItemLabel={setItemLabel}
                        situations={situations}
                        selectedSituation={selectedSituation}
                        setSelectedSituation={setSelectedSituation}
                        handleSave={handleSaveItem}
                        isEditing={currentItemIndex !== null}
                    />
                )}


                {/* --- Drag and Drop Area --- */}
                <DragDropContext onDragEnd={onDragEnd}>
                    {/* Outer Droppable for Sections */}
                    <Droppable droppableId="toc-sections" type="section">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className={styles.sectionsContainer}>
                                {currentSections.map((section, sectionIndex) => (
                                    <Draggable key={`section-${sectionIndex}`} draggableId={`section-${sectionIndex}`} index={sectionIndex}>
                                        {(providedDraggable, snapshotDraggable) => (
                                            <div
                                                ref={providedDraggable.innerRef}
                                                {...providedDraggable.draggableProps}
                                                className={`${styles.section} ${snapshotDraggable.isDragging ? styles.dragging : ''}`}
                                            >
                                                {/* Section Header */}
                                                <div className={styles.sectionHeader}>
                                                    {/* Drag Handle */}
                                                     <span {...providedDraggable.dragHandleProps} className={styles.dragHandleIconContainer} title="Drag to reorder section">
                                                        <FaGripVertical className={styles.dragHandleIcon} />
                                                    </span>
                                                    <div className={styles.sectionTitleContainer}>
                                                        {/* Conditional Rendering for Editing */}
                                                        {isEditingSection && currentSectionIndex === sectionIndex ? (
                                                             <div className={styles.sectionEdit} ref={inputRef}>
                                                                <input
                                                                    type="text"
                                                                    value={sectionLabel}
                                                                    onChange={(e) => setSectionLabel(e.target.value)}
                                                                    onKeyPress={(e) => handleSectionEditKeyPress(e, sectionIndex)}
                                                                    placeholder="Section Title"
                                                                    className={styles.inputField}
                                                                    aria-label="Edit section title"
                                                                    autoFocus
                                                                />
                                                                 <button onClick={() => handleSectionEditConfirm(sectionIndex)} className={`${styles.button} ${styles.buttonIcon} ${styles.saveButton}`} title="Save Title">
                                                                    <FaSave />
                                                                </button>
                                                                 <button onClick={cancelEditing} className={`${styles.button} ${styles.buttonIcon} ${styles.cancelButton}`} title="Cancel Edit">
                                                                    <FaTimes />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                             <span
                                                                className={styles.sectionTitle}
                                                                onClick={() => startEditing(sectionIndex, section.label || "")}
                                                                title="Click to edit title"
                                                                role="button"
                                                                tabIndex={0}
                                                                onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') startEditing(sectionIndex, section.label || ""); }} 
                                                                >
                                                                {section.label || "Untitled Section"}
                                                            </span>
                                                        )}
                                                    </div>
                                                     {/* Actions only visible when NOT editing this section */}
                                                     {!isEditingSection || currentSectionIndex !== sectionIndex ? (
                                                        <div className={styles.sectionActions}>
                                                            <button onClick={(event) => toggleModal(event, sectionIndex)} className={`${styles.button} ${styles.buttonText}`} title="Add Situation to Section">
                                                                <FaPlus /> Add Situation
                                                            </button>
                                                            <button onClick={() => handleDeleteSection(sectionIndex)} className={`${styles.button} ${styles.buttonIcon} ${styles.deleteButton}`} title="Delete Section">
                                                                <FaTrashAlt />
                                                            </button>
                                                        </div>
                                                    ) : null } {/* Hide actions while editing */}
                                                </div>

                                                {/* Section Content (Droppable for Items) */}
                                                <Droppable droppableId={`section-${sectionIndex}`} type="item">
                                                    {(providedDroppable, snapshotDroppable) => (
                                                        <div
                                                            ref={providedDroppable.innerRef}
                                                            {...providedDroppable.droppableProps}
                                                            className={`${styles.sectionContent} ${snapshotDroppable.isDraggingOver ? styles.draggingOver : ''}`}
                                                        >
                                                            {/* Ensure section.items is an array before mapping */}
                                                            {(section.items || []).map((item, itemIndex) => (
                                                                <Draggable key={`item-${sectionIndex}-${itemIndex}`} draggableId={`item-${sectionIndex}-${itemIndex}`} index={itemIndex}>
                                                                    {(providedItem, snapshotItem) => (
                                                                        <div
                                                                            ref={providedItem.innerRef}
                                                                            {...providedItem.draggableProps}
                                                                            className={`${styles.itemContainer} ${snapshotItem.isDragging ? styles.dragging : ''}`}
                                                                        >
                                                                             {/* Item Drag Handle */}
                                                                             <span {...providedItem.dragHandleProps} className={styles.itemDragHandle} title="Drag to reorder situation">
                                                                                <FaGripVertical className={styles.dragHandleIcon} />
                                                                            </span>
                                                                            <div
                                                                                className={styles.itemLabel}
                                                                                onClick={(event) => toggleModal(event, sectionIndex, itemIndex, item)}
                                                                                title="Click to edit"
                                                                                role="button"
                                                                                tabIndex={0}
                                                                                onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleModal(e, sectionIndex, itemIndex, item); }}
                                                                            >
                                                                                {item.label || "Untitled Situation"}
                                                                            </div>
                                                                            <button onClick={() => handleDeleteItem(sectionIndex, itemIndex)} className={`${styles.button} ${styles.buttonIcon} ${styles.deleteButton}`} title="Delete Situation">
                                                                                <FaMinus />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                            {providedDroppable.placeholder}
                                                            {(!section.items || section.items.length === 0) && !snapshotDroppable.isDraggingOver && (
                                                                <div className={styles.emptySectionMessage}>
                                                                    Drag situations here or click 'Add Situation' above.
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                {currentSections.length === 0 && !isSectionInputVisible && (
                                    <div className={styles.emptyTocMessage}>
                                        No sections created yet. Click 'Create Section' to begin.
                                    </div>
                                )}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
};

export default TableOfContents;